import { useEffect, useState, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, X } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccessPage = ({ refreshSubscription }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const hasPolled = useRef(false);

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;

    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      navigate('/subscription');
    }
  }, [location.search, navigate]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshSubscription();
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment:", error);
      setStatus('error');
    }
  };

  return (
    <div className="max-w-md mx-auto text-center animate-fade-in py-12">
      {status === 'checking' && (
        <>
          <div className="spinner mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-stone-900">Processing your payment...</h2>
          <p className="text-stone-500 mt-2">Please wait while we confirm your subscription.</p>
        </>
      )}
      {status === 'success' && (
        <>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-green-900">Payment Successful!</h2>
          <p className="text-stone-500 mt-2">Redirecting to home...</p>
        </>
      )}
      {(status === 'failed' || status === 'timeout' || status === 'error') && (
        <>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-red-900">Payment Issue</h2>
          <p className="text-stone-500 mt-2 mb-4">There was a problem processing your payment.</p>
          <NavLink to="/subscription">
            <Button className="btn-primary">Try Again</Button>
          </NavLink>
        </>
      )}
    </div>
  );
};

export default SubscriptionSuccessPage;
